import React, { useState } from 'react';
import axios from 'axios';
import { ShiftMethodSummary } from './ShiftMethodSummary';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, Check, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CLOSING_METHODS, countedToBreakdown } from '../lib/closingMethods';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Operador não vê o esperado: mostra as formas do PDV (sem a linha "Cartão legado")
const METHODS = CLOSING_METHODS.filter((m) => m.key !== 'cartao');

/**
 * Fechamento "às cegas" — operador comum informa os valores recebidos por forma
 * de pagamento SEM ver os totais reais. O resultado (esperado, diferença) é
 * exibido SOMENTE para admin/manager — operador comum só vê "Caixa fechado".
 */
export const BlindCloseForm = ({ sessionId, onClosed, onError }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setValues({ ...values, [k]: v });

  const totalInformed = METHODS.reduce((s, m) => s + (parseFloat(values[m.key] || '0') || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (totalInformed === 0) {
      onError && onError('Informe o valor recebido em pelo menos uma forma de pagamento (use 0 para zero).');
      return;
    }
    setLoading(true);
    try {
      // Endpoint:
      // - sessionId presente → fechamento de uma sessão específica (admin/manager finaliza pending_close)
      // - sessionId ausente → fecha a própria sessão atual
      const endpoint = sessionId
        ? `${API}/cash-sessions/${sessionId}/close-by-admin`
        : `${API}/cash-sessions/close`;
      const r = await axios.post(endpoint, {
        counted_amount: parseFloat(values.dinheiro || '0') || 0,
        notes: notes || null,
        counted_breakdown: countedToBreakdown(values),
      }, { withCredentials: true });
      setResult(r.data);
    } catch (err) {
      onError && onError(err.response?.data?.detail || 'Erro ao fechar caixa');
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    // Para NÃO-admin: oculta esperado/diferença. Mostra só confirmação.
    if (!isAdmin) {
      return (
        <div className="mt-4 p-5 rounded-md border-2 bg-emerald-50 border-emerald-400" data-testid="blind-close-result-operator">
          <div className="flex items-center gap-3 mb-3">
            <Check size={36} className="text-emerald-700" />
            <div>
              <h3 className="text-2xl font-black text-slate-900">✓ Turno encerrado!</h3>
              <p className="text-sm text-slate-700">Os valores informados foram registrados.</p>
            </div>
          </div>
          <div className="bg-white rounded p-3 border mb-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-600">Total informado por você</p>
            <p className="text-2xl font-bold text-slate-900">R$ {Number(result.counted_amount || 0).toFixed(2)}</p>
          </div>
          <div className="mb-3">
            <ShiftMethodSummary sessionId={result.id} />
          </div>
          <p className="text-xs text-slate-500 mb-3">
            💡 A conciliação final (contado × esperado e diferenças) é feita pelo administrador.
          </p>
          <Button onClick={() => { setResult(null); onClosed && onClosed(); }} className="w-full bg-slate-700 hover:bg-slate-800" data-testid="blind-close-ok">
            OK
          </Button>
        </div>
      );
    }
    // Para admin: mostra esperado e diferença normalmente.
    // difference = TOTAL (soma das diferenças de todas as formas); cash_difference = só dinheiro
    const diff = (result.difference !== undefined && result.difference !== null) ? result.difference : ((result.counted_amount || 0) - (result.expected_amount || 0));
    const cashDiff = (result.cash_difference !== undefined && result.cash_difference !== null) ? result.cash_difference : ((result.counted_amount || 0) - (result.expected_amount || 0));
    const ok = Math.abs(diff) < 0.01;
    const sobrou = diff > 0;
    const partial = ok && Math.abs(cashDiff) >= 0.01;
    return (
      <div className={`mt-4 p-5 rounded-md border-2 ${ok ? 'bg-emerald-50 border-emerald-400' : (sobrou ? 'bg-blue-50 border-blue-400' : 'bg-amber-50 border-amber-400')}`} data-testid="blind-close-result">
        <div className="flex items-center gap-3 mb-3">
          {ok ? <Check size={36} className="text-emerald-700" /> : <AlertTriangle size={36} className={sobrou ? 'text-blue-700' : 'text-amber-700'} />}
          <div>
            <h3 className="text-2xl font-black text-slate-900">
              {ok ? (partial ? '✓ Caixa bateu no total (formas compensaram)' : '✓ Caixa bateu!') : (sobrou ? 'Sobrou em caixa' : 'Faltou em caixa')}
            </h3>
            <p className="text-sm text-slate-600">Resultado do fechamento — soma de todas as formas de pagamento</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className="bg-white rounded p-3 border">
            <p className="text-[10px] uppercase tracking-wider text-slate-600">Dinheiro informado</p>
            <p className="text-xl font-bold text-slate-900">R$ {Number(result.counted_amount || 0).toFixed(2)}</p>
            <p className="text-[11px] text-slate-500">esperado R$ {Number(result.expected_amount || 0).toFixed(2)}</p>
          </div>
          <div className="bg-white rounded p-3 border">
            <p className="text-[10px] uppercase tracking-wider text-slate-600">Diferença só no dinheiro</p>
            <p className={`text-xl font-bold ${Math.abs(cashDiff) < 0.01 ? 'text-emerald-700' : cashDiff > 0 ? 'text-blue-700' : 'text-amber-700'}`} data-testid="blind-close-cash-diff">{cashDiff > 0 ? '+' : ''}R$ {Number(cashDiff).toFixed(2)}</p>
          </div>
          <div className={`bg-white rounded p-3 border-2 ${ok ? 'border-emerald-400' : sobrou ? 'border-blue-400' : 'border-amber-400'}`}>
            <p className="text-[10px] uppercase tracking-wider text-slate-600">Diferença TOTAL (todas as formas)</p>
            <p className={`text-xl font-black ${ok ? 'text-emerald-700' : sobrou ? 'text-blue-700' : 'text-amber-700'}`} data-testid="blind-close-total-diff">
              {diff > 0 ? '+' : ''}R$ {diff.toFixed(2)}
            </p>
          </div>
        </div>
        <Button onClick={() => { setResult(null); onClosed && onClosed(); }} className="w-full bg-slate-700 hover:bg-slate-800">
          OK, voltar
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-4 bg-slate-50 rounded-md border border-slate-300 p-4" data-testid="blind-close-form">
      <h3 className="text-lg font-bold text-slate-900 mb-1">Contagem por Forma de Pagamento</h3>
      <p className="text-xs text-slate-600 mb-3">
        Informe o quanto entrou de cada forma. Use <strong>0</strong> se não recebeu naquela forma. O resultado é mostrado só após confirmar.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {METHODS.map((m) => (
          <div key={m.key} className="flex items-center gap-2 bg-white rounded border border-slate-200 p-2">
            <Label className="w-32 font-medium text-sm">{m.label}</Label>
            <Input
              type="number" step="0.01" min="0"
              value={values[m.key] || ''}
              onChange={(e) => set(m.key, e.target.value)}
              placeholder="0.00"
              className="flex-1"
              data-testid={`blind-close-${m.key}`}
            />
          </div>
        ))}
      </div>
      <div className="bg-white rounded p-2 border border-slate-300 mt-2 flex items-center justify-between">
        <span className="text-sm font-bold text-slate-700">Total informado:</span>
        <span className="text-xl font-black text-slate-900" data-testid="blind-close-total">R$ {totalInformed.toFixed(2)}</span>
      </div>
      <div className="mt-2">
        <Label>Observações</Label>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Opcional" />
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-red-600 hover:bg-red-700 mt-3 h-11" data-testid="blind-close-submit">
        <Lock size={16} className="mr-2" /> {loading ? 'Fechando...' : 'Confirmar Fechamento'}
      </Button>
    </form>
  );
};

export default BlindCloseForm;
